import {
  CssColor,
  CssFont,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  IStyleParam,
  StyleParamType,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  booleanParam,
  buildStringStyleParams,
  colorParam,
  colorParamWithOpacity,
  fontParam,
  getRadiusCssValue,
  getStringStyleParamsValue,
  numberParam,
  numberParamWithMobileDefault,
  stringEnumParam,
} from './styleParamsUtils';
import {
  AlignmentOptions,
  ImagePositionOptions,
  ImageShapeOptions,
  ListLayoutStyleOptions,
  VerticalAlignmentOptions,
  WidgetLayoutOptions,
} from './types';

export type IStylesParams = {
  titleAlignment: StyleParamType.Font;
  serviceDescriptionTextAlign: StyleParamType.Font;
  maxCardsPerRow: StyleParamType.Number;
  cardSpacing: StyleParamType.Number;
  layoutType: StyleParamType.Font;
  listLayoutStyle: StyleParamType.Font;
  sideBySideImagePosition: StyleParamType.Font;
  sideBySideAlternateSides: StyleParamType.Boolean;
  verticalTextAlignment: StyleParamType.Font;
  sideBySideImageSize: StyleParamType.Number;
  sideBySideImagePadding: StyleParamType.Number;
  onImageItemHeight: StyleParamType.Number;
  onImageOverlayColor: StyleParamType.Color;
  dividerColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerCornerRadius: StyleParamType.Number;
  serviceImageOpacity: StyleParamType.Number;
  serviceImageBackgroundColor: StyleParamType.Color;
  serviceImageCornerRadius: StyleParamType.Number;
  serviceImageCornerRadii: StyleParamType.Font;
  serviceImageCornerRadiiLinked: StyleParamType.Boolean;
  isServiceImageCornerRadiusUniform: StyleParamType.Boolean;
  serviceImageBorderColor: StyleParamType.Color;
  serviceImageBorderWidth: StyleParamType.Number;
  serviceImagePadding: StyleParamType.Number;
  borderWidth: StyleParamType.Number;
  serviceImageShape: StyleParamType.Font;
  borderColor: StyleParamType.Color;
  cornerRadius: StyleParamType.Number;
  cornerRadii: StyleParamType.Font;
  cornerRadiiLinked: StyleParamType.Boolean;
  isCornerRadiusUniform: StyleParamType.Boolean;
  gridVerticalPadding: StyleParamType.Number;
  gridSidePadding: StyleParamType.Number;
  serviceDescriptionColor: StyleParamType.Color;
  serviceDescriptionFont: StyleParamType.Font;
  mobileServiceDescriptionFontSize: StyleParamType.Number;
  infoBackgroundColor: StyleParamType.Color;
  serviceNameColor: StyleParamType.Color;
  serviceNameFont: StyleParamType.Font;
  mobileServiceNameFontSize: StyleParamType.Number;
  serviceNumberColor: StyleParamType.Color;
  serviceNumberFont: StyleParamType.Font;
  readMoreColor: StyleParamType.Color;
  readMoreFont: StyleParamType.Font;
  widgetTitleFont: StyleParamType.Font;
  mobileWidgetTitleFontSize: StyleParamType.Number;
  widgetTitleColor: StyleParamType.Color;
  widgetBackgroundColor: StyleParamType.Color;
  // Card shadow styles
  cardShadowEnabled: StyleParamType.Boolean;
  cardShadowAngle: StyleParamType.Number;
  cardShadowDistance: StyleParamType.Number;
  cardShadowBlur: StyleParamType.Number;
  cardShadowColor: StyleParamType.Color;
  // display styles
  _imageDisplay: StyleParamType.Boolean;
  _serviceDescriptionDisplay: StyleParamType.Boolean;
  _serviceNumberDisplay: StyleParamType.Boolean;
  _titleDisplay: StyleParamType.Boolean;
};

const getImageAspectRatio = ({
  isMobile,
  serviceImageShape,
}: {
  isMobile: boolean;
  serviceImageShape: ImageShapeOptions;
}) =>
  isMobile
    ? serviceImageShape === ImageShapeOptions.SQUARE
      ? 1
      : 1.52
    : serviceImageShape === ImageShapeOptions.SQUARE
    ? 1
    : 1.5;

const cornerRadiiParam = (
  oldParamName: string,
): Omit<IStyleParam<StyleParamType.Font>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) => {
    const oldParam = {
      ...numberParam(StyleType.borderRadius, 0),
      name: oldParamName,
    } as IStyleParam;
    const oldRadius = (getStyleParamValue(oldParam) as number) || 0;
    return buildStringStyleParams(
      `${oldRadius} ${oldRadius} ${oldRadius} ${oldRadius}`,
    );
  },
  metadata: {
    type: StyleType.borderRadius as StyleType.UNKNOWN_CssPropertyType,
    defaultValue: {
      type: 'static' as const,
      value: '0 0 0 0',
    },
  },
});

const serviceImageCornerRadiiLinkedParam = createStylesParam(
  'serviceImageCornerRadiiLinked',
  booleanParam(true),
);

const cornerRadiiLinkedParam = createStylesParam(
  'cornerRadiiLinked',
  booleanParam(true),
);

// The list layout's "card" sub-layout gets a boxed look by default — a border,
// rounded corners and a padded image — whereas "strip" stays flat. These are
// defaults *only*: the runtime resolves getDefaultValue() into the style params
// before customCssVars runs (generateTPAParams.overrideStyleParamsWithDefaults),
// so they apply while the param is untouched, a value the user sets always wins,
// and switching list styles only re-styles cards the user hasn't customized.
const LIST_CARD_BORDER_WIDTH = 1;
const LIST_CARD_IMAGE_PADDING = 12;
const LIST_CARD_CORNER_RADII = '8 8 8 8';

const layoutTypeParam = createStylesParam(
  'layoutType',
  stringEnumParam<WidgetLayoutOptions>(
    WidgetLayoutOptions.GRID,
    StyleType.stringEnum,
  ),
);

const listLayoutStyleParam = createStylesParam(
  'listLayoutStyle',
  stringEnumParam<ListLayoutStyleOptions>(
    ListLayoutStyleOptions.STRIP,
    StyleType.stringEnum,
  ),
);

const isListCardLayout = (
  getStyleParamValue?: (param: IStyleParam) => unknown,
): boolean => {
  if (typeof getStyleParamValue !== 'function') {
    return false;
  }
  const layoutType = (getStyleParamValue(layoutTypeParam) as { value?: string })
    ?.value;
  const listLayoutStyle = (
    getStyleParamValue(listLayoutStyleParam) as { value?: string }
  )?.value;
  return (
    layoutType === WidgetLayoutOptions.LIST &&
    listLayoutStyle === ListLayoutStyleOptions.CARD
  );
};

// Wrap a param so the list "card" sub-layout resolves to its own default while
// every other layout keeps the wrapped param's default. The wrapped param's
// static metadata default is preserved, so builder-model resolution (which
// reads metadata rather than getDefaultValue) safely falls back to the
// non-card default.
const withListCardDefault = <P extends Omit<IStyleParam, 'name'>>(
  base: P,
  cardDefaultValue: ReturnType<NonNullable<P['getDefaultValue']>>,
): P => ({
  ...base,
  getDefaultValue: (args) =>
    isListCardLayout(args?.getStyleParamValue)
      ? cardDefaultValue
      : base.getDefaultValue!(args),
});

export const styleParamsDefinitions: {
  [key in keyof IStylesParams]: IStyleParam;
} = {
  titleAlignment: stringEnumParam<AlignmentOptions>(
    AlignmentOptions.LEFT,
    StyleType.textAlign,
  ),
  serviceDescriptionTextAlign: stringEnumParam<AlignmentOptions>(
    AlignmentOptions.LEFT,
    StyleType.textAlign,
  ),
  maxCardsPerRow: numberParamWithMobileDefault(StyleType.number, 1, 3),
  cardSpacing: numberParamWithMobileDefault(StyleType.padding, 20, 32),
  layoutType: stringEnumParam<WidgetLayoutOptions>(
    WidgetLayoutOptions.GRID,
    StyleType.stringEnum,
  ),
  listLayoutStyle: stringEnumParam<ListLayoutStyleOptions>(
    ListLayoutStyleOptions.STRIP,
    StyleType.stringEnum,
  ),
  sideBySideImagePosition: stringEnumParam<ImagePositionOptions>(
    ImagePositionOptions.LEFT,
    StyleType.stringEnum,
  ),
  sideBySideAlternateSides: booleanParam(false),
  verticalTextAlignment: stringEnumParam<VerticalAlignmentOptions>(
    VerticalAlignmentOptions.TOP,
    StyleType.stringEnum,
  ),
  sideBySideImageSize: numberParam(
    StyleType.number,
    50,
    'Side-by-side layout image width as percentage of the item: integer 20–80. 50 = image and text split evenly.',
  ),
  sideBySideImagePadding: numberParam(StyleType.padding, 0),
  onImageItemHeight: numberParam(
    StyleType.number,
    190,
    'On-image layout item height in pixels: integer 100–600.',
  ),
  onImageOverlayColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-5',
    0.4,
    CssColor.WstParagraph2Color,
  ),
  dividerColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  dividerWidth: numberParam(StyleType.borderWidth, 1),
  dividerCornerRadius: numberParam(StyleType.borderRadius, 0),
  serviceImageOpacity: numberParam(
    StyleType.number,
    100,
    'Service image opacity: integer 0–100 only (no %). 0 = fully hidden, 100 = fully visible; values in between scale linearly.',
  ),
  serviceImageBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-5',
    0.2,
    CssColor.WstSecondaryBackgroundColor,
  ),
  serviceImageCornerRadius: numberParam(StyleType.borderRadius, 0),
  serviceImageCornerRadii: withListCardDefault(
    cornerRadiiParam('serviceImageCornerRadius'),
    buildStringStyleParams(LIST_CARD_CORNER_RADII),
  ),
  serviceImageCornerRadiiLinked: booleanParam(true),
  isServiceImageCornerRadiusUniform: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(serviceImageCornerRadiiLinkedParam) as boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'ref' as const,
        ref: 'serviceImageCornerRadiiLinked',
      },
    },
  },
  serviceImageBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    1,
    CssColor.WstParagraph2Color,
  ),
  serviceImageBorderWidth: numberParam(StyleType.borderWidth, 0),
  serviceImagePadding: withListCardDefault(
    numberParam(StyleType.padding, 0),
    LIST_CARD_IMAGE_PADDING,
  ),
  borderWidth: withListCardDefault(
    numberParam(StyleType.borderWidth, 0),
    LIST_CARD_BORDER_WIDTH,
  ),
  serviceImageShape: stringEnumParam<ImageShapeOptions>(
    ImageShapeOptions.RECTANGLE,
    StyleType.stringEnum,
  ),
  borderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  cornerRadius: numberParam(StyleType.borderRadius, 0),
  cornerRadii: withListCardDefault(
    cornerRadiiParam('cornerRadius'),
    buildStringStyleParams(LIST_CARD_CORNER_RADII),
  ),
  cornerRadiiLinked: booleanParam(true),
  isCornerRadiusUniform: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(cornerRadiiLinkedParam) as boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'ref' as const,
        ref: 'cornerRadiiLinked',
      },
    },
  },
  gridVerticalPadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 32),
  },
  gridSidePadding: {
    inheritDesktop: false,
    ...numberParamWithMobileDefault(StyleType.padding, 20, 0),
  },
  serviceDescriptionColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  serviceDescriptionFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  mobileServiceDescriptionFontSize: numberParam(StyleType.fontSize, 14),
  infoBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceNameFont: fontParam(
    'Page-title',
    { size: 24, htmlTag: 'h2' },
    CssFont.WstHeading2Font,
  ),
  serviceNameColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  mobileServiceNameFontSize: numberParam(StyleType.fontSize, 20),
  serviceNumberColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  serviceNumberFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'span' },
    CssFont.WstParagraph2Font,
  ),
  readMoreColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  readMoreFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'span' },
    CssFont.WstParagraph2Font,
  ),
  widgetTitleFont: fontParam(
    'Page-title',
    { size: 32, htmlTag: 'h1' },
    CssFont.WstHeading2Font,
  ),
  mobileWidgetTitleFontSize: numberParam(StyleType.fontSize, 24),
  widgetTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  widgetBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  // Card shadow styles
  cardShadowEnabled: booleanParam(false),
  cardShadowAngle: numberParam(StyleType.number, 90),
  cardShadowDistance: numberParam(StyleType.number, 0),
  cardShadowBlur: numberParam(StyleType.number, 25),
  cardShadowColor: colorParamWithOpacity(
    StyleType.color,
    'color-5',
    0.2,
    CssColor.WstParagraph2Color,
  ),
  // display styles
  _imageDisplay: booleanParam(true),
  _titleDisplay: booleanParam(true),
  _serviceDescriptionDisplay: booleanParam(true),
  _serviceNumberDisplay: booleanParam(true),
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);

enum DisplayStyles {
  UNSET = 'unset',
  NONE = 'none',
}

export const customCssVars: CustomCssVarsFn = (args) => {
  const { isMobile, styleParams } = args;

  const serviceImageShape = getStringStyleParamsValue(
    styleParams.strings.serviceImageShape,
  ) as ImageShapeOptions;

  const imageAspectRatio = getImageAspectRatio({
    isMobile,
    serviceImageShape,
  });

  const numericStyles = styleParams.numbers;
  const isRoundImage = serviceImageShape === ImageShapeOptions.ROUND;

  const layoutType =
    (getStringStyleParamsValue(
      styleParams.strings.layoutType,
    ) as WidgetLayoutOptions) || WidgetLayoutOptions.GRID;

  const verticalTextAlignment =
    (getStringStyleParamsValue(
      styleParams.strings.verticalTextAlignment,
    ) as VerticalAlignmentOptions) || VerticalAlignmentOptions.TOP;

  const sideBySideImagePosition =
    (getStringStyleParamsValue(
      styleParams.strings.sideBySideImagePosition,
    ) as ImagePositionOptions) || ImagePositionOptions.LEFT;

  // All layout switching happens here (per breakpoint), never in component
  // TypeScript: the widget renders one DOM structure and these vars restyle
  // it into grid / list / side-by-side / on-image.
  const getLayoutVars = (): Record<string, string | number> => {
    const verticalAlignmentMap = {
      [VerticalAlignmentOptions.TOP]: 'flex-start',
      [VerticalAlignmentOptions.CENTER]: 'center',
      [VerticalAlignmentOptions.BOTTOM]: 'flex-end',
    } as const;
    const infoVerticalAlignment = verticalAlignmentMap[verticalTextAlignment];

    switch (layoutType) {
      case WidgetLayoutOptions.LIST: {
        const listLayoutStyle =
          (getStringStyleParamsValue(
            styleParams.strings.listLayoutStyle,
          ) as ListLayoutStyleOptions) || ListLayoutStyleOptions.STRIP;
        return {
          cardsPerRow: 1,
          evenImageDirection: isMobile ? 'column' : 'row',
          oddImageDirection: isMobile ? 'column' : 'row',
          mediaWidth: isMobile ? '100%' : '30%',
          mediaFlex: isMobile ? 0 : '0 0 auto',
          infoFlex: 1,
          infoStackDisplay: 'flex',
          cardGap: '32px',
          // padding-top % resolves against the card width, so scale the
          // aspect ratio by the media width share to size media to the image
          imagePaddingTop: isMobile
            ? `${Math.round(100 / imageAspectRatio)}%`
            : `${Math.round((100 / imageAspectRatio) * 0.3)}%`,
          dividerDisplay:
            listLayoutStyle === ListLayoutStyleOptions.STRIP ? 'block' : 'none',
          infoVerticalAlignment,
        };
      }
      case WidgetLayoutOptions.SIDE_BY_SIDE: {
        const direction =
          sideBySideImagePosition === ImagePositionOptions.RIGHT
            ? 'row-reverse'
            : 'row';
        const alternate = styleParams.booleans
          .sideBySideAlternateSides as boolean;
        const flip = direction === 'row' ? 'row-reverse' : 'row';
        return {
          cardsPerRow: 1,
          evenImageDirection: isMobile ? 'column' : direction,
          oddImageDirection: isMobile ? 'column' : alternate ? flip : direction,
          mediaWidth: isMobile
            ? `calc(100% - ${numericStyles.sideBySideImagePadding * 2}px)`
            : `${numericStyles.sideBySideImageSize}%`,
          mediaFlex: isMobile ? 0 : '0 0 auto',
          infoFlex: 1,
          infoStackDisplay: 'flex',
          imagePaddingTop: isMobile
            ? `${Math.round(100 / imageAspectRatio)}%`
            : `${Math.round(
                (100 / imageAspectRatio) *
                  (numericStyles.sideBySideImageSize / 100),
              )}%`,
          evenOverlappingImageMargin: `${numericStyles.sideBySideImagePadding}px`,
          oddOverlappingImageMargin: `${numericStyles.sideBySideImagePadding}px`,
          // media stretches to the row height; shave the margins off so the
          // image (and its rounded corners) stays inside the overflow-hidden card
          mediaHeight: `calc(100% - ${
            numericStyles.sideBySideImagePadding * 2
          }px)`,
          infoVerticalAlignment,
        };
      }
      case WidgetLayoutOptions.ON_IMAGE:
        return {
          cardsPerRow: 1,
          itemOverflow: 'hidden',
          onImageItemHeight: `${numericStyles.onImageItemHeight}px`,
          onImageOverlayColor: styleParams.colors.onImageOverlayColor,
          overlayDisplay: 'block',
          mediaPosition: 'absolute',
          mediaInset: '0',
          mediaWidth: '100%',
          mediaHeight: '100%',
          imagePaddingTop: '0',
          infoPosition: 'absolute',
          infoInset: '0',
          infoZIndex: 2,
          infoStackDisplay: 'flex',
          infoBackgroundOverride: 'transparent',
          infoVerticalAlignment,
        };
      case WidgetLayoutOptions.GRID:
      default:
        return {};
    }
  };

  const getDisplayStyle = (key: string) => {
    const value = styleParams.booleans[key] as boolean;
    return value ? DisplayStyles.UNSET : DisplayStyles.NONE;
  };

  const getCardShadowCss = () => {
    const isEnabled = styleParams.booleans.cardShadowEnabled as boolean;
    if (!isEnabled) {
      return 'none';
    }

    const angle = numericStyles.cardShadowAngle;
    const distance = numericStyles.cardShadowDistance;
    const blur = numericStyles.cardShadowBlur;

    // Convert angle to x/y offsets (angle is in degrees, 0 is up, clockwise)
    const angleRad = ((angle - 90) * Math.PI) / 180;
    const offsetX = Math.round(Math.cos(angleRad) * distance);
    const offsetY = Math.round(Math.sin(angleRad) * distance);

    const colorStr = styleParams.colors.cardShadowColor;

    // Parse RGB/RGBA string (e.g., "rgb(0, 0, 0)" or "rgba(0, 0, 0, 0.25)")
    const rgbaMatch = colorStr.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/,
    );
    if (rgbaMatch) {
      const r = parseInt(rgbaMatch[1], 10) || 0;
      const g = parseInt(rgbaMatch[2], 10) || 0;
      const b = parseInt(rgbaMatch[3], 10) || 0;
      const a = parseFloat(rgbaMatch[4]);
      return `${offsetX}px ${offsetY}px ${blur}px rgba(${r}, ${g}, ${b}, ${a})`;
    }
    return '';
  };

  const getServiceTitleAlignmentVars = () => {
    switch (
      getStringStyleParamsValue(
        styleParams.strings.titleAlignment,
      ) as AlignmentOptions
    ) {
      case AlignmentOptions.CENTER:
        return {
          titlePaddingLeft: '10%',
          titlePaddingRight: '10%',
          titleTextAlign: 'center',
        };
      case AlignmentOptions.LEFT:
        return {
          titlePaddingLeft: 'initial',
          titlePaddingRight: '20%',
          titleTextAlign: 'left',
        };
      case AlignmentOptions.RIGHT:
        return {
          titlePaddingLeft: '20%',
          titlePaddingRight: 'initial',
          titleTextAlign: 'right',
        };
    }
  };

  const getServiceDescriptionAlignmentVars = () => {
    switch (
      getStringStyleParamsValue(
        styleParams.strings._serviceDescriptionTextAlign,
      ) as AlignmentOptions
    ) {
      case AlignmentOptions.CENTER:
        return {
          serviceDescriptionTextAlign: 'center',
        };
      case AlignmentOptions.LEFT:
        return {
          serviceDescriptionTextAlign: 'left',
        };
      case AlignmentOptions.RIGHT:
        return {
          serviceDescriptionTextAlign: 'right',
        };
    }
  };

  return {
    // Grid styles
    maxCardsPerRow: isMobile ? 1 : numericStyles.maxCardsPerRow,
    imagePaddingTop: imageAspectRatio
      ? `${Math.round(100 / imageAspectRatio)}%`
      : `${100}%`,
    cardDisplay: 'flex',
    layoutCardMinWidth: '130px',
    infoWidth: 'inherit',
    layoutVerticalPadding: isMobile
      ? numericStyles.gridVerticalPadding
      : numericStyles.gridVerticalPadding - numericStyles.borderWidth,
    layoutSidePadding: isMobile
      ? numericStyles.gridSidePadding
      : numericStyles.gridSidePadding - numericStyles.borderWidth,
    mediaRatio: 0,
    infoRatio: 1,
    infoOverflow: 'unset',
    imageOpacity: numericStyles.serviceImageOpacity / 100,
    imageBorderWidth: numericStyles.serviceImageBorderWidth,
    imageWrapperRadius: isRoundImage
      ? '50%'
      : getRadiusCssValue(styleParams.strings.serviceImageCornerRadii),
    imageCardCornerRadius: 0,
    imageWrapperMargin: isRoundImage
      ? `${32 - numericStyles.borderWidth}px auto 0`
      : `${numericStyles.serviceImagePadding}px`,
    imageWrapperHeight: `calc(100% - ${
      isRoundImage ? 32 : numericStyles.serviceImagePadding * 2
    }px)`,
    imageWrapperWidth: isRoundImage
      ? `calc(${100 / imageAspectRatio}% - 32px)`
      : `calc(100% - ${numericStyles.serviceImagePadding * 2}px)`,
    cardBorderWidth: numericStyles.borderWidth,
    stripCalcIndication: '0px',
    nonStripCalcIndication: '1px',
    mediaFlex: 0,
    infoFlex: 1,
    backgroundOnHover: 'inherit',
    cornerRadius: getRadiusCssValue(styleParams.strings.cornerRadii),
    infoCardCornerRadius: 0,
    gridTemplateRows: 'unset',
    infoBorderWidth: 0,
    infoZIndex: 'auto',
    rootBackgroundColor: styleParams.colors.infoBackgroundColor,
    evenImageDirection: 'column',
    oddImageDirection: 'column',
    evenOverlappingImageMargin: 0,
    oddOverlappingImageMargin: 0,
    evenOverlappingGridArea: 'unset',
    oddOverlappingGridArea: 'unset',
    evenOverlappingTemplateColumns: 'unset',
    oddOverlappingTemplateColumns: 'unset',
    mediaWidth: '100%',
    mediaHeight: '100%',
    ...getServiceTitleAlignmentVars(),
    // Layout vars: grid defaults here; getLayoutVars() overrides per layout.
    // Vars absent for grid (dividerDisplay, overlayDisplay, mediaPosition,
    // infoPosition, infoStackDisplay, ...) fall back to grid-equivalent
    // defaults defined in the stylesheets.
    cardsPerRow: isMobile ? 1 : numericStyles.maxCardsPerRow,
    dividerColor: styleParams.colors.dividerColor,
    dividerWidth: numericStyles.dividerWidth,
    dividerRadius: `${numericStyles.dividerCornerRadius}px`,
    infoVerticalAlignment: 'flex-start',
    ...getLayoutVars(),
    // ...getServiceDescriptionAlignmentVars(),
    // display styles
    imageDisplay: getDisplayStyle('_imageDisplay'),
    titleDisplay: getDisplayStyle('_titleDisplay'),
    serviceDescriptionDisplay: getDisplayStyle('_serviceDescriptionDisplay'),
    serviceNumberDisplay: getDisplayStyle('_serviceNumberDisplay'),
    // Card shadow
    cardShadow: getCardShadowCss(),
  };
};
